import type {
  ProjectListItem,
  ProjectStatus,
} from "@/modules/management/project/domain/types";
import { projectStatusProgressFillClass } from "@/modules/management/project/presentation/lib/project-status-progress-styles";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import { format } from "date-fns";
import { Eye, MoreVertical, Pencil, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

function formatDate(s: string) {
  try {
    return format(new Date(s), "dd/MM/yyyy");
  } catch {
    return s;
  }
}

/** Status badges: active = blue, completed = green, on_hold = gray */
function statusBadgeVariant(
  status: ProjectStatus,
): "default" | "secondary" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "completed":
      return "default";
    case "on_hold":
      return "secondary";
    default:
      return "outline";
  }
}

function statusBadgeClassName(status: ProjectStatus): string {
  switch (status) {
    case "active":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30";
    case "completed":
      return "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30";
    case "on_hold":
      return "bg-muted text-muted-foreground";
    default:
      return "";
  }
}

function statusLabel(status: ProjectStatus): string {
  switch (status) {
    case "active":
      return "ກຳລັງດຳເນີນການ";
    case "completed":
      return "ສຳເລັດ";
    case "on_hold":
      return "ຢຸດ";
    default:
      return status;
  }
}

function levelLabel(row: ProjectListItem): string {
  const level = row.level ?? "organization";
  if (level === "position" && row.positionName) return row.positionName;
  if (level === "department" && row.departmentName) return row.departmentName;
  switch (level) {
    case "position":
      return "ລະດັບຕຳແໜ່ງ";
    case "department":
      return "ລະດັບຜະແນກ";
    case "organization":
    default:
      return "ລະດັບອົງກອນ";
  }
}

type ProjectTableProps = {
  data: ProjectListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: ProjectListItem) => void;
  onEdit: (row: ProjectListItem) => void;
  onDelete?: (row: ProjectListItem) => void;
};

export function ProjectTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
}: ProjectTableProps) {
  const columns: TanstackReactTable.ColumnDef<ProjectListItem>[] = [
    createSortableColumn<ProjectListItem>("name", "ຊື່ໂຄງການ", {
      size: 220,
      cell: ({ row }) => (
        <div>
          {onView ? (
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => onView(row.original)}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="font-medium">{row.original.name}</span>
          )}
        </div>
      ),
    }),
    createSortableColumn<ProjectListItem>("startDate", "ວັນເລີ່ມ", {
      size: 110,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.startDate)}
        </span>
      ),
    }),
    createSortableColumn<ProjectListItem>("endDate", "ວັນສິ້ນສຸດ", {
      size: 110,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.endDate)}
        </span>
      ),
    }),
    createSortableColumn<ProjectListItem>("level", "ລະດັບ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {levelLabel(row.original)}
        </span>
      ),
    }),
    createSortableColumn<ProjectListItem>("responsibleUserName", "ຜູ້ຮັບຜິດຊອບ", {
      size: 180,
      cell: ({ row }) => {
        const name = row.original.responsibleUserName ?? null;
        const image = row.original.responsibleUserImage;
        if (!name && !row.original.responsibleUserId) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        const src = resolveImageSrc(image ?? undefined) ?? undefined;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={src} alt={name ?? "avatar"} />
              <AvatarFallback className="text-xs">
                {name ? getInitials(name) : "—"}
              </AvatarFallback>
            </Avatar>
            <span className="truncate text-muted-foreground text-sm">
              {name ?? "—"}
            </span>
          </div>
        );
      },
    }),
    createSortableColumn<ProjectListItem>("progressPercent", "% ຄວາມຄືບໜ້າ", {
      size: 140,
      cell: ({ row }) => {
        const pct = Math.min(100, Math.max(0, row.original.progressPercent));
        const fill = projectStatusProgressFillClass(row.original.status);
        return (
          <div className="flex min-w-[80px] max-w-[120px] flex-col gap-1">
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`ຄວາມຄືບໜ້າ ${pct}%`}
            >
              <div
                className={cn(
                  "h-full rounded-full transition-[width] duration-200",
                  fill,
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-muted-foreground text-xs tabular-nums">
              {pct}%
            </span>
          </div>
        );
      },
    }),
    createSortableColumn<ProjectListItem>("status", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge
          variant={statusBadgeVariant(row.original.status)}
          className={statusBadgeClassName(row.original.status)}
        >
          {statusLabel(row.original.status)}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        const items: {
          label: string;
          onClick: () => void;
          icon?: ReactNode;
        }[] = [];
        if (onView) {
          items.push({
            label: "ລາຍລະອຽດ",
            onClick: () => onView(r),
            icon: <Eye className="h-4 w-4" />,
          });
        }
        items.push({
          label: "ແກ້ໄຂ",
          onClick: () => onEdit(r),
          icon: <Pencil className="h-4 w-4" />,
        });
        if (onDelete) {
          items.push({
            label: "ລຶບໂຄງການ",
            onClick: () => onDelete(r),
            icon: <Trash2 className="h-4 w-4" />,
          });
        }
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="ການກະທຳ"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {items.map((item) => {
                const isDelete = item.label === "ລຶບໂຄງການ";
                return (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={item.onClick}
                    className={
                      isDelete
                        ? "text-destructive focus:text-destructive"
                        : undefined
                    }
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      size: 80,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        const s = sorting[0];
        if (s?.id) onSortingChange(s.id, !!s.desc);
      }}
    />
  );
}
